import FilterSelect from "@/components/FilterSelect";
import FilterGroup from "@/components/FilterSelect/FilterGroup";
import FollowButton from "@/components/FollowButton";
import { followType } from "@/components/FollowButton/operationButton";
import HBEmpty from "@/components/HBEmpty";
import HBIcon from "@/components/HBIcon";
import JsonLd from "@/components/JsonLd";
import { ScrollHeader } from "@/components/Layout/ScrollHeader";
import PinList from "@/components/PinList";
import ShareDropdown from "@/components/ShareDropdown";
import UserList from "@/components/UserList";
import { API_CACHEKEY } from "@/constants/cacheKey";
import { COLOR_ITEM_MAP, COLOR_LIST } from "@/constants/color";
import { EVENT } from "@/constants/event";
import { FILE_TYPE_ITEM_MAP, FILE_TYPE_LIST } from "@/constants/fileType";
import { TIME_ITEM_MAP, TIME_LIST } from "@/constants/filterTime";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { RESOLUTION_ITEM_MAP, RESOLUTION_LIST } from "@/constants/resolution";
import { SORT_BY_HOT_ITEM_MAP, SORT_BY_HOT_LIST } from "@/constants/sort";
import useAppStore from "@/context/store";
import { BoardPageHeader } from "@/pageComponents/BoardPage/Header";
import {
  BoardInviteToast,
  PrivateBoardInviteView,
} from "@/pageComponents/BoardPage/Invite";
import RecommendPins from "@/pageComponents/BoardPage/RecommendPins";
import { useBoardAction } from "@/pageComponents/BoardPage/useBoardAction";
import ImmersivePinList from "@/pageComponents/BoardPinList/ImmersivePinList";
import {
  getBoard,
  getBoardByInviteCode,
  getBoardFollowers,
  getBoardInfo,
  getBoardMembers,
  getBoardPins,
  getPin,
  getUserPins,
} from "@/services";
import { getSeoInlinks } from "@/services/qingzhou";
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import {
  canCollabBoardMng,
  getBoardType,
  isWaitClassifyBoard,
} from "@/utils/board";
import { useEnableMultiBoard } from "@/utils/hooks/useMultiBoard";
import { useIsMaterialVip } from "@/utils/hooks/useVip";
import imageUrl from "@/utils/imageUrl";
import { isElementInViewport } from "@/utils/isElementInViewport";
import { checkCanUploadPin, openBindTelModal } from "@/utils/open";
import { safeParse } from "@/utils/safeParse";
import { isLogined } from "@/utils/server/isLogined";
import {
  serverSideNotFound,
  serverSideRedirect,
} from "@/utils/server/serverSide";
import strEllipsis from "@/utils/strEllipsis";
import { uidFieldName } from "@/utils/uid";
import useReq from "@/utils/useReq";
import useSaveListCache from "@/utils/useSaveListCache";
import {
  useEventListener,
  useMemoizedFn,
  useMount,
  useSetState,
  useUpdateEffect,
} from "ahooks";
import { Button, Tabs } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { findIndex, isEqual, last, omit, throttle } from "lodash";
import Head from "next/head";
import { useRouter } from "next/router";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Else, If, Then, When } from "react-if";
import styles from "./index.module.less";

const PIN_SIZE_PER_REQUEST = 40; // 采集列表，每次请求的分页大小

const TabPane = Tabs.TabPane;
const sortType = {
  SEQ: "seq", // 自定义排序
  TIME_ASC: "asc", // 时间升序
  TIME_DESC: "desc", // 时间降序
  HOT: "weight", // 热度排序
};

function BoardEmpty({ type = "pin" }) {
  if (type === "pin") {
    return (
      <HBEmpty
        size="small"
        image="//cdn.dancf.com/fe-assets/img/6a5695b2efbda504b26a212d160553ec.svg"
        title="还没有采集哦~"
      />
    );
  } else {
    return (
      <HBEmpty
        size="small"
        image="//cdn.dancf.com/fe-assets/img/6a5695b2efbda504b26a212d160553ec.svg"
        title="还没有被关注哦~"
      />
    );
  }
}

const PrevNum = 7;
async function queryBeforeViewSource(viewSourcePinId, boardId) {
  if (viewSourcePinId && boardId) {
    const [pinRes, prevRes] = await Promise.all([
      getPin({ pin_id: viewSourcePinId }),
      getBoardPins({
        board_id: boardId,
        limit: PrevNum,
        since: viewSourcePinId,
      }),
    ]);

    pinRes.pin.mask = true;

    return [pinRes.pin, ...prevRes.pins].reverse();
  }

  return [];
}

function BoardPinList({ board, serverSidePins, query }) {
  const boardId = board?.board_id;
  const { user, headerHeight, column } = useAppStore((store) => ({
    user: store.user,
    headerHeight: store.headerHeight,
    column: store.column,
  }));
  const router = useRouter();
  const isVip = useIsMaterialVip();

  const [hasMore, setHasMore] = useState(serverSidePins?.length !== 0);
  const [dataSource, setDataSource] = useState(serverSidePins || []);
  const [historyMode, setHistoryMode] = useState(false);
  const [historyPinId, setHistoryPinId] = useState(null);

  const { viewType, color, fileType, sort, resolution } = query;

  const viewSource = router.query.viewSource;
  const [time, setTime] = useState();

  const pageRef = useRef(serverSidePins ? 2 : 1);
  const maxRef = useRef(last(serverSidePins)?.pin_id || 0);
  const pinRef = useRef();
  const hasInitData = useRef(serverSidePins?.length > 0);

  const isFilter = useMemoizedFn(() => {
    return color || fileType || resolution || sort === sortType.HOT;
  });

  const viewSourceRef = useRef(viewSource);
  const queryBefore = useMemoizedFn(() => {
    if (viewSourceRef.current) {
      return queryBeforeViewSource(viewSourceRef.current, boardId);
    }

    return [];
  });

  useUpdateEffect(() => {
    setDataSource((prev) =>
      prev.map((item) => ({
        ...item,
        board,
      }))
    );
  }, [board]);

  const backFirstPin = useMemoizedFn(() => {
    resetQuery();
    maxRef.current = 0;
    setDataSource([]);
    queryAction();
    setIsLocationed(false);
  });

  const [isLoctioned, setIsLocationed] = useState(false);
  // 定位刚刚访问的采集
  const locationViewSource = useMemoizedFn(() => {
    const source = viewSourceRef.current;
    const el = document.querySelector(`[data-pin-id="${source}"]`);
    if (el) {
      // 已在页面就直接滚动到视图
      router
        .replace({
          query: {
            sort: sortType.TIME_DESC,
            ...omit(router.query, ["viewSource"]),
          },
        })
        .then(() => {
          el.scrollIntoView();
        });
    } else {
      setDataSource([]);
      maxRef.current = source;
      queryAction(true);
      router.replace({
        query: {
          sort: sortType.TIME_DESC,
          ...omit(router.query, ["viewSource"]),
        },
      });
    }

    setIsLocationed(true);
  });

  const resetQuery = useMemoizedFn(() => {
    setDataSource([]);
    setHasMore(true);
    pageRef.current = 1;
    maxRef.current = 0;
  });

  // 查询loading， 控制触发多次搜索
  const [queryLoading, setQueryLoading] = useState(false);
  const queryAction = useMemoizedFn(async (hasBefore = false) => {
    if (!boardId || isNaN(+boardId) || queryLoading) {
      return;
    }

    setQueryLoading(true);

    let result;

    if (isFilter()) {
      // 有搜索条件且排序方式不等于时间排序走ES，搜索接口
      result = await getUserPins({
        username: board.user.urlname,
        board_id: boardId,
        colors: color,
        file_type: fileType,
        resolution,
        per_page: PIN_SIZE_PER_REQUEST,
        page: pageRef.current,
        ...(sort && sort !== sortType.HOT
          ? { "sort[created_at]": sort }
          : { sort }),
      });
    } else {
      const [date_since, date_until] = time?.split("-") || [];
      const [boardPinRes, prev] = await Promise.all([
        getBoardPins({
          board_id: boardId,
          limit: PIN_SIZE_PER_REQUEST,
          sort: sort || sortType.SEQ,
          ...(sort && sort === sortType.TIME_ASC
            ? { since: maxRef.current || 0 }
            : { max: maxRef.current || null }),
          date_since,
          date_until,
        }),
        hasBefore ? queryBefore() : [],
      ]);

      result = {
        ...boardPinRes,
        pins: [...prev, ...(boardPinRes?.pins || [])],
      };
    }

    setQueryLoading(false);
    const pins =
      result?.pins.map((pin) => ({
        ...pin,
        mask: pin.pin_id === Number(viewSource),
        board,
      })) || [];

    if (pins.length <= 0) {
      setHasMore(false);
    }

    setDataSource((prev) => prev.concat(pins));
  });

  const onLoadMore = useMemoizedFn(() => {
    if (isFilter()) {
      // 筛选器使用页码分页
      pageRef.current += 1;
      queryAction();
    } else {
      if (dataSource.length > 0) {
        const lastPin = last(dataSource);
        maxRef.current = lastPin.seq || lastPin.pin_id;
        queryAction();
      }
    }
  });

  const onAddPin = useMemoizedFn(async () => {
    if (!checkCanUploadPin(user)) {
      return;
    }

    await openBindTelModal();

    const { PinUploaderModal } = await import("@/components/PinCreator");

    PinUploaderModal.open({
      name: "pinUploaderModal",
      targetBoardId: boardId,
      onOk: () => {
        pageRef.current = 1;
        maxRef.current = 0;
        setDataSource([]);
        setHasMore(true);
        queryAction();
      },
    });
  });

  const isOwner = useMemo(() => {
    if (!board?.user?.user_id || !user?.user_id) {
      return false;
    }
    return board?.user?.user_id === user?.user_id;
  }, [board, user]);

  // 奇葩的待归类画板
  const isSpecial = isWaitClassifyBoard(board);

  const addPinComponent = useMemo(() => {
    const component = (
      <div
        className={classNames(styles.add, {
          [styles.fullWidth]: viewType === "fullwidth",
        })}
        onClick={onAddPin}
      >
        <div className={styles.icon}>
          <HBIcon type="add" />
        </div>
        <div className={styles.text}>上传采集</div>
      </div>
    );
    return () => {
      return component;
    };
  }, [onAddPin, viewType]);

  // const onChangeViewType = useCallback(
  //   (_) => {
  //     router.push({
  //       page: "/boards/[boardId]",
  //       query: {
  //         ...router.query,
  //         viewType: _?.value,
  //       },
  //     });
  //   },
  //   [router]
  // );

  const onChangeFilter = useMemoizedFn((option, field) => {
    if (field === "sort" && option.value === sortType.SEQ) {
      if (router.query?.sort === sortType.SEQ || !router.query?.sort) {
        return;
      }

      router.push({
        query: {
          sort: option.value,
          boardId: boardId,
        },
      });
    } else {
      const nextQuery = Object.entries({
        ...omit(router.query, ["viewSource"]),
        [field]: option?.value,
      }).reduce((result, [key, value]) => {
        if (value) {
          result[key] = value;
        }

        return result;
      }, {});

      if (["color", "fileType", "resolution"].includes(field) && isSeqSort) {
        nextQuery.sort = sortType.TIME_DESC;
      }

      if (isEqual(nextQuery, router.query)) {
        return;
      }

      router.push(
        {
          page: "/boards/[boardId]",
          query: nextQuery,
        },
        undefined,
        {
          shallow: true,
        }
      );
    }

    setHistoryMode(false);
    // 修改排序则清除记录
    if (field === "sort") {
      const historyStr = window.localStorage.getItem(
        LOCAL_STORAGE_KEY.BOARD_HISTORY
      );
      const history = historyStr ? safeParse(historyStr) : {};
      delete history[boardId];
      window.localStorage.setItem(
        LOCAL_STORAGE_KEY.BOARD_HISTORY,
        JSON.stringify(history)
      );
      setHistoryPinId(null);
    }
    resetQuery();
  });
  const onChangeTimeFilter = (option) => {
    setTime(option?.value);
    resetQuery();
  };

  // 页面进入请求第一页数据
  useMount(() => {
    if (!hasInitData.current) {
      queryAction();
    }
  });

  // 筛选条件、画板变更，进行重置更新数据
  useUpdateEffect(() => {
    queryAction();
  }, [boardId, viewType, color, fileType, sort, resolution, time]);

  // 展示刚刚访问按钮
  const [showViewSource, setShowViewSource] = useState();
  useEffect(() => {
    const index = findIndex(
      dataSource,
      (item) => item.pin_id === Number(viewSource)
    );
    if (index === -1) {
      // 采集没在加载列表里
      setShowViewSource(true);
    } else {
      if (index < column) {
        // 在第一行
        setShowViewSource(false);
      } else {
        const time = setTimeout(() => {
          // 判断采集是否在可视范围内
          const el = document.querySelector(`[data-pin-id="${viewSource}"]`);
          setShowViewSource(!isElementInViewport(el));
        }, 200);

        return () => clearTimeout(time);
      }
    }
  }, [column, dataSource, viewSource]);

  useEventListener(EVENT.HIDE_MASK, () => {
    setShowViewSource(false);
  });

  const { sortOption, sortMap } = useMemo(() => {
    if (board?.extra?.seq_order !== 1) {
      return {
        sortOption: SORT_BY_HOT_LIST.filter((item) => item.value !== "seq"),
        sortMap: omit(SORT_BY_HOT_ITEM_MAP, ["seq"]),
      };
    } else {
      return {
        sortOption: SORT_BY_HOT_LIST,
        sortMap: SORT_BY_HOT_ITEM_MAP,
      };
    }
  }, [board?.extra?.seq_order]);

  const isSeqSort = query.sort === sortType.SEQ;

  const defaultSort = isFilter() ? sortType.TIME_DESC : sortType.SEQ;

  const enableHistory = !(
    isFilter() ||
    (isSeqSort && board?.extra?.seq_order === 1) ||
    sort === sortType.TIME_ASC
  );

  // 当前选中了筛选器进行筛选，除了排序与视图
  const hasFilter = color || fileType || resolution || time;

  const showRecommenPins = useMemo(() => {
    return !hasFilter && !hasMore && !isWaitClassifyBoard(board);
  }, [hasFilter, hasMore, board]);

  /**
   * MARK: 浏览历史记录相关
   */
  useEffect(() => {
    const onHistory = throttle(() => {
      if (!enableHistory || window.scrollY < window.screen.height * 5) {
        return;
      }

      // 只取第一个瀑布流容器，避免画板推荐采集影响
      const container = document.querySelector(".infinite-scroll-component");

      if (!container) {
        return;
      }

      const domList = [...container.querySelectorAll("[data-pin-id]")];
      const firstIndex = domList.findIndex((item) => {
        const rect = item?.getBoundingClientRect();
        return rect?.bottom > headerHeight + 64;
      });
      let firstItem = domList[firstIndex];
      // 判断是不是最后一条
      if (firstIndex >= domList.length - 1) {
        if (domList.length > 1) {
          // 列表长度大于1，取前一条
          firstItem = domList[firstIndex - 1];
        } else {
          // 列表长度只有一条，那取null
          firstItem = null;
        }
      }
      const historyStr = window.localStorage.getItem(
        LOCAL_STORAGE_KEY.BOARD_HISTORY
      );
      const history = historyStr ? safeParse(historyStr) : {};
      if (firstItem) {
        window.localStorage.setItem(
          LOCAL_STORAGE_KEY.BOARD_HISTORY,
          JSON.stringify({
            ...history,
            [boardId]: {
              pinId: firstItem?.getAttribute("data-pin-id"),
              date: +new Date(),
            },
          })
        );
      }
    }, 1000);

    const onChangePinBoard = (evt) => {
      if (evt?.detail?.originBoardId === boardId) {
        setDataSource((dataSource) =>
          dataSource.filter((record) => record.pin_id !== evt?.detail?.pinId)
        );
      }
    };

    window.addEventListener("scroll", onHistory, { passive: true });
    window.addEventListener(EVENT.CHANGE_PIN_BOARD, onChangePinBoard);

    return () => {
      window.removeEventListener("scroll", onHistory, { passive: true });
      window.removeEventListener(EVENT.CHANGE_PIN_BOARD, onChangePinBoard);
    };
  }, [headerHeight, sort, boardId, color, fileType, resolution, enableHistory]);

  useEffect(() => {
    if (!boardId || isNaN(+boardId)) {
      return;
    }
    const oldDataStr = window.localStorage.getItem("huaban-board-history");
    let oldData = null;
    if (oldDataStr) {
      oldData = safeParse(oldDataStr, () => {
        console.error("更新画板历史记录失败");
      });
    }
    const dataStr = window.localStorage.getItem(
      LOCAL_STORAGE_KEY.BOARD_HISTORY
    );
    try {
      let data = dataStr ? JSON.parse(dataStr) : {};
      if (oldData) {
        Object.entries(oldData).forEach(([key, value]) => {
          data[key] = {
            pinId: value,
            date: +new Date(),
          };
        });
        window.localStorage.setItem(
          LOCAL_STORAGE_KEY.BOARD_HISTORY,
          JSON.stringify(data)
        );
        window.localStorage.removeItem("huaban-board-history");
      }
      data = Object.entries(data)
        .reduce((result, [key, value]) => {
          if (value.date > +dayjs().subtract(1, "month")) {
            result.push({
              ...value,
              boardId: key,
            });
          }
          return result;
        }, [])
        .sort((a, b) => b.date - a.date)
        .slice(0, 1000)
        .reduce((result, item) => {
          const { boardId, ...other } = item;
          result[boardId] = other;
          return result;
        }, {});
      const pinId = data[boardId]?.pinId;
      if (pinId) {
        setHistoryPinId(pinId);
      }
    } catch (e) {
      console.error("获取画板历史记录失败");
    }
  }, [boardId]);

  const onGoHistory = useMemoizedFn(() => {
    maxRef.current = historyPinId;
    setDataSource([]);
    setHasMore(true);
    router.replace({
      query: {
        ...omit(router.query, ["viewSource"]),
        sort: sortType.TIME_DESC,
      },
    });
    setHistoryMode(true);
    queryAction();
  });

  const onBackHistory = useCallback(() => {
    resetQuery();
    queryAction();
    setHistoryMode(false);
    const historyStr = window.localStorage.getItem(
      LOCAL_STORAGE_KEY.BOARD_HISTORY
    );
    const history = historyStr ? safeParse(historyStr) : {};
    delete history[boardId];
    window.localStorage.setItem(
      LOCAL_STORAGE_KEY.BOARD_HISTORY,
      JSON.stringify(history)
    );
    setHistoryPinId(null);
  }, [boardId, queryAction, resetQuery]);

  const canAddPin = useMemo(() => {
    return (isOwner || board.is_collab_member) && !isSpecial;
  }, [isOwner, board, isSpecial]);

  return (
    <div
      className={styles.pin}
      ref={pinRef}
      style={{ marginTop: canAddPin ? 64 : 20 }}
    >
      {viewSource && showViewSource && (
        <div
          className={styles.pageTip}
          onClick={locationViewSource}
          data-gd-click="button_click"
          data-button-name="刚刚访问"
        >
          刚刚访问 <HBIcon type="ic_unfold" />
        </div>
      )}
      {isLoctioned && (
        <div
          className={styles.pageTip}
          onClick={backFirstPin}
          data-gd-click="button_click"
          data-button-name="回到第1张采集"
        >
          回到第 1 张采集 <HBIcon type="ic_fold" />
        </div>
      )}
      <div
        className={styles.operation}
        style={canAddPin ? {} : { display: "none" }}
      >
        {/* 
        时间排序和其他筛选项冲突，所以当选择时间排序时，其他筛选项被禁用掉，反之一样
        */}
        <FilterGroup>
          <FilterSelect
            className={styles.filter}
            name="排序"
            allowClear={false}
            options={sortOption}
            value={
              isSeqSort && isFilter()
                ? sortMap[sortType.TIME_DESC]
                : sortMap[sort || defaultSort]
            }
            disabled={time}
            onChange={(option) => onChangeFilter(option, "sort")}
          />

          <FilterSelect
            className={styles.filter}
            name="格式"
            options={FILE_TYPE_LIST}
            value={FILE_TYPE_ITEM_MAP[fileType]}
            disabled={time}
            onChange={(option) => onChangeFilter(option, "fileType")}
          />

          <FilterSelect
            className={styles.filter}
            name="尺寸"
            options={RESOLUTION_LIST}
            disabled={time}
            value={RESOLUTION_ITEM_MAP[resolution]}
            onChange={(option) => onChangeFilter(option, "resolution")}
          />
          <FilterSelect
            className={styles.filter}
            name={"颜色"}
            options={COLOR_LIST}
            iconType="img"
            optionType="img"
            disabled={time}
            value={COLOR_ITEM_MAP[color]}
            onChange={(option) => onChangeFilter(option, "color")}
          />
          <FilterSelect
            className={styles.filter}
            name="时间"
            options={TIME_LIST}
            value={TIME_ITEM_MAP[time]}
            onChange={(option) => onChangeTimeFilter(option)}
            disabled={isFilter() || sort !== sortType.TIME_DESC}
          />

          {/* <FilterSelect
            className={styles.filter}
            name="视图"
            options={VIEWTYPE1_LIST}
            value={VIEWTYPE1_ITEM_MAP[viewType]}
            onChange={onChangeViewType}
          /> */}
        </FilterGroup>
      </div>
      <When condition={historyPinId && enableHistory}>
        <If condition={historyMode}>
          <Then>
            <div className={styles.history}>
              <div className={styles.desc}>已定位到你上次浏览的位置，</div>
              <div className={styles.link} onClick={onBackHistory}>
                <HBIcon className={styles.icon} type="replay" />
                <div className={styles.text}>回到最新</div>
              </div>
            </div>
          </Then>
          <Else>
            <div className={styles.history}>
              <div
                className={styles.link}
                onClick={onGoHistory}
                data-gd-click="button_click"
                data-button-name="浏览定位"
                data-board-id={board?.board_id}
              >
                <HBIcon
                  className={styles.icon}
                  type="ic_schedule2"
                  style={{ marginRight: "5px" }}
                />
                <div className={styles.text}>点击回到上次浏览的位置</div>
              </div>
            </div>
          </Else>
        </If>
      </When>

      <If condition={dataSource.length > 0 || canAddPin || hasMore}>
        <Then>
          {viewType !== "fullwidth" ? (
            <PinList
              dataSource={dataSource}
              hasMore={hasMore}
              showBoardLink={false}
              hideCount={!isOwner}
              showMaterialTag={isVip}
              onLoadMore={onLoadMore}
              endMessage={showRecommenPins ? null : undefined}
              onLoginLimit={() => {
                setHasMore(false);
              }}
              hideUser={!(board?.members?.length > 1)}
              currentBoard={board}
              enableCurrentBoardPriority={true}
            >
              {canAddPin && addPinComponent()}
            </PinList>
          ) : (
            <ImmersivePinList
              dataSource={dataSource}
              hasMore={hasMore}
              hideUser={true}
              showBoardLink={false}
              onLoadMore={onLoadMore}
            >
              {canAddPin && addPinComponent()}
            </ImmersivePinList>
          )}
        </Then>
        <Else>
          <BoardEmpty type="pin" />
        </Else>
      </If>
      {/* 画板内推荐采集，原创、待归类画板不展示 */}
      {showRecommenPins && (
        <RecommendPins
          boardId={boardId}
          pins={dataSource.slice(0, 100).map((item) => ({
            file_id: item.file_id,
          }))}
          currentBoard={board}
        />
      )}
    </div>
  );
}

function BoardUserList({ board }) {
  const boardId = board?.board_id;
  const [query, setQuery] = useSetState({
    max: 0,
  });
  const [hasMore, setHasMore] = useState(true);
  const { data: dataSource = [] } = useReq(
    async () => {
      const { max } = query;

      if (!boardId) {
        return;
      }

      const result = await getBoardFollowers({
        board_id: boardId,
        limit: 20,
        ...(max ? { max } : null),
      });

      const followers = result?.followers || [];

      if (followers.length <= 0) {
        setHasMore(false);
      }

      return dataSource.concat(followers);
    },
    {
      refreshDeps: [query, boardId],
    }
  );

  const onLoadMore = () => {
    if (dataSource.length > 0) {
      const last = dataSource[dataSource.length - 1];
      setQuery({
        max: last.seq || last.user_id,
      });
    }
  };

  return (
    <div className={styles.user}>
      <If condition={dataSource.length > 0 || hasMore}>
        <Then>
          <UserList
            dataSource={dataSource}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
          />
        </Then>
        <Else>
          <BoardEmpty type="user" />
        </Else>
      </If>
    </div>
  );
}

export async function getServerSideProps({ params, req, res, query }) {
  const { invite_code } = query;

  const { boardId } = params;

  if (invite_code) {
    const result = await getBoardByInviteCode({
      invite_code,
      cookie: req.headers.cookie || "",
    });

    if (result.err || !result.board || !result.invitation) {
      return serverSideRedirect(res, {
        destination: "/boards/expired",
        permanent: false,
      });
    }

    if (
      result.is_member ||
      // 邀请者是当前用户
      req?.cookies?.[uidFieldName] === String(result.inviter?.user_id)
    ) {
      return serverSideRedirect(res, {
        destination: "/boards/" + boardId,
        permanent: false,
      });
    }

    const { board, inviter } = result;
    return {
      props: {
        serverSideBoard: board,
        inviter,
        memberLimit: result.member_limit,
        memberTotal: result.member_total,
        isInviteExpired:
          result.invitation.status === 3 ||
          result.invitation.expires_at < Date.now() / 1000,
      },
    };
  }

  // 已登录用户不使用SSR
  if (isLogined(req)) {
    return {
      props: {},
    };
  }

  if (isNaN(+boardId)) {
    return serverSideNotFound(res);
  }

  // 获取画板
  const boardResult = await getBoardInfo(boardId);
  if (boardResult.err === 404) {
    return serverSideNotFound(res);
  }
  const { board } = boardResult;
  const { color, fileType, resolution, sort } = query;
  try {
    const [pinsResult, inlinks] = await Promise.all([
      color || fileType || resolution || sort === sortType.HOT
        ? getUserPins({
            username: board.user.urlname,
            board_id: boardId,
            colors: color,
            file_type: fileType,
            resolution,
            per_page: PIN_SIZE_PER_REQUEST,
            page: 1,

            ...(sort && sort !== sortType.HOT
              ? { "sort[created_at]": sort }
              : { sort }),
          })
        : getBoardPins({
            board_id: boardId,
            limit: PIN_SIZE_PER_REQUEST,
            ...(sort && sort === sortType.TIME_ASC
              ? { since: 0 }
              : {
                  sort: sortType.SEQ,
                }),
          }),
      getSeoInlinks({
        title: `${board.title}_${board?.category_name}图片素材_${board?.user?.username}的画板-花瓣网`,
        current_url: req.url,
      }),
    ]);

    if (pinsResult.err) {
      console.error(
        `getBoardPins request error: ${pinsResult.err}-${pinsResult.msg}`
      );
      return {
        serversideBoard: board,
        serverSidePins: [],
      };
    }

    // 获取画板的采集
    const serverSidePins = pinsResult?.pins?.map((item) => ({
      board,
      mask: item.pin_id === Number(query.viewSource),
      ...item,
    }));

    return {
      props: {
        serversideBoard: board,
        serverSidePins,
        inlinks,
      },
    };
  } catch (error) {
    console.error(`getBoardPins request error: ${error}`);
    return {
      props: {
        serversideBoard: board,
        serverSidePins: [],
      },
    };
  }
}

export default function Board({
  serverSideBoard,
  serverSidePins,
  inviter,
  isInviteExpired,
  memberLimit,
  memberTotal,
}) {
  const router = useRouter();
  const {
    boardId,
    backUrl,
    tab: _tab,
    viewType,
    color,
    sort = sortType.SEQ,
    fileType,
    resolution,
    invite_code,
  } = router.query;

  const { user, userReady, headerHeight } = useAppStore(
    ({ user, userReady, headerHeight }) => ({
      user,
      userReady,
      headerHeight,
    })
  );

  // 当前邀请者就是当前用户, 跳转到画板详情页
  useLayoutEffect(() => {
    if (user?.user_id && user?.user_id === inviter?.user_id) {
      router.replace("/boards/" + boardId);
    }
  }, [boardId, inviter?.user_id, router, user?.user_id]);

  const boardAction = useBoardAction(serverSideBoard, {
    onExitBoard: () => {
      mutateMembers((prev) => prev.filter((m) => m.user_id !== user.user_id));
    },
    onRemoveMember: (user) => {
      mutateMembers((prev) => prev.filter((m) => m.user_id !== user.user_id));
    },
  });

  const { board, setBoard, following, onToggleFollow, onBoardEdit } =
    boardAction;

  const {
    data: members,
    mutate: mutateMembers,
    refreshAsync: refreshMembers,
  } = useReq(
    async () => {
      if (!board) {
        return null;
      }

      // 没有协作成员
      if (!board?.extra?.collab_members) return [];

      // 获取所有协作成员
      const res = await getBoardMembers({ board_id: board.board_id });
      return res.data || [];
    },
    {
      refreshDeps: [board?.board_id],
    }
  );

  const tabRef = useRef(null);
  useSaveListCache();

  const { runAsync: getBoardCache } = useReq(getBoard, {
    cacheKey: `${API_CACHEKEY.BOARD}_${boardId}`,
    staleTime: 3000,
    manual: true,
  });
  const { run: getBoardInfo } = useReq(
    async () => {
      if (!boardId) {
        return;
      }

      if (isNaN(+boardId)) {
        router.push("/404", `/boards/${boardId}`);
        return;
      }

      setBoard(null);

      const result = await getBoardCache({
        board_id: boardId,
      });

      const boardInfo = result?.board || null;

      if (result.err === 404) {
        router.push("/404", `/boards/${boardId}`);
      } else {
        setBoard(boardInfo);
      }
    },
    {
      manual: true,
    }
  );
  // 首次获取画板信息
  useMount(() => {
    // 引导删除，删除对应的key值
    localStorage.removeItem("hb_new_user_board_follow_btn_tooltip");

    // 服务端渲染不用请求
    if (
      serverSideBoard &&
      (serverSideBoard?.user || serverSideBoard?.is_private > 0)
    ) {
      return;
    }

    getBoardInfo();
  });

  const showInvitePage = useMemo(() => {
    return inviter && board?.is_private > 0;
  }, [board?.is_private, inviter]);

  // 异步上报pv埋点
  useEffect(() => {
    function reportPv() {
      // 地址匹配才上报pv
      if (window.location.pathname === "/boards/" + board.board_id) {
        pvTracker({
          ...getPageData(),
          scene_name: board.title,
          board_type: getBoardType(board),
          board_id: board.board_id,
          ...(showInvitePage
            ? {
                page_nm:
                  board?.is_private > 0 ? "隐私画板邀请页" : "多人画板邀请页",
              }
            : null),
        });
      }
    }

    if (board) {
      reportPv();
    }

    // 监听从采集详情弹窗返回的路由变化，上报pv
    // 不上报pv会导致返回来的页面埋点page_nm不对
    window.addEventListener("popstate", reportPv);

    return () => {
      window.removeEventListener("popstate", reportPv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board?.board_id]);

  // 更新画板信息
  useUpdateEffect(() => {
    getBoardInfo();
  }, [boardId]);

  const tab = useMemo(() => {
    return _tab || "pin";
  }, [_tab]);

  const onChangeTab = useCallback(
    (tab) => {
      router.push({
        page: router.asPath,
        query: {
          ...router.query,
          tab,
        },
      });
    },
    [router]
  );

  // 奇葩的待归类画板
  const isSpecial = board?.is_private === 2;

  const pics = useMemo(() => {
    return board?.pins?.map((pin) => imageUrl(pin.file)) || [];
  }, [board]);

  const goBatch = useCallback(() => {
    router.push({
      pathname: `/boards/edit/${board?.board_id}`,
      query: {
        ...(fileType ? { fileType } : null),
        ...(color ? { color } : null),
        ...(resolution ? { resolution } : null),
      },
    });
  }, [board, color, fileType, resolution, router]);

  const canEditBoard = useMemo(() => {
    return canCollabBoardMng(board, user);
  }, [board, user]);

  const { setEnable: setEnableMultiBoard } = useEnableMultiBoard();

  useMount(() => {
    if (inviter) {
      setEnableMultiBoard(true);
    }
  });

  const onInviteAccept = useMemoizedFn(async () => {
    router.push("/boards/" + boardId);
    // 延迟获取画板信息，等待后端更新画板信息
    const result = await getBoard({
      board_id: boardId,
    });

    if (result?.board) {
      setBoard(result?.board);
      refreshMembers();
    }
  });

  const pageTitle = `${board?.title}图片大全-${board?.title}高清好看的图片--花瓣${board?.user?.username}的画板`;
  const pageDesc = `花瓣网为你找到${board?.pin_count}个精选${board?.title}高清图片与素材，${board?.title}图片大全已赢得${board?.follow_count}位用户关注与喜爱，轻松获取创意灵感，助你快速完成设计。`;
  const pageKeywords = `${board?.title}图片,${board?.title}高清图片,${board?.category_name}好看图片`;

  return (
    <div
      className={classNames(styles.board, "calculateWrapper")}
      data-content-source="信息流"
      data-board-id={board?.board_id}
      data-board-type={getBoardType(board)}
    >
      <JsonLd
        data={{
          "@id": `https://huaban.com/boards/${board?.board_id}`,
          title: pageTitle,
          images: serverSidePins
            ?.slice(0, 3)
            .map((pin) => imageUrl(pin.file, { width: 240 })),
          description: pageDesc,
          pubDate:
            board?.created_at &&
            new Date(board.created_at * 1000).toISOString(),
          upDate:
            board?.updated_at &&
            new Date(board?.updated_at * 1000).toISOString(),
        }}
      />

      <Head>
        <title>{board ? pageTitle : "画板-花瓣网"}</title>
        {!!board && <meta name="keywords" content={pageKeywords} />}
        {!!board && <meta name="description" content={pageDesc} />}
      </Head>

      <ScrollHeader headerHeight={headerHeight} tabRef={tabRef} isTop={true}>
        <div className={classNames(styles.wrapper, "calculateWrapper")}>
          <div className={styles.left}>
            <When condition={backUrl}>
              <Button
                className={styles.button}
                type="link"
                onClick={() => router.push(backUrl)}
                icon={
                  <HBIcon className={styles.icon} type="ic_arrowThinLeft" />
                }
              />
            </When>
            <div className={styles.title}>{board?.title}</div>
          </div>
          <div className={styles.right}>
            <If condition={isSpecial}>
              <Then>
                <ShareDropdown
                  placement="bottomCenter"
                  pics={pics}
                  title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                    board?.title,
                    45
                  )}相关的设计灵感，来看看：`}
                >
                  <Button
                    className={classNames([styles.button, styles.rect])}
                    type="text"
                    data-gd-click="button_click"
                    data-button-name="分享"
                    data-board-id={board?.board_id}
                  >
                    <HBIcon className={styles.icon} type="hb_share_new" />
                  </Button>
                </ShareDropdown>
                <When condition={canEditBoard}>
                  <Button
                    type="text"
                    className={classNames([styles.button, styles.rect])}
                    data-gd-click="button_click"
                    data-button-name="批量管理"
                    data-board-id={board?.board_id}
                    onClick={goBatch}
                  >
                    <HBIcon className={styles.icon} type="batch1" />
                  </Button>
                </When>
              </Then>
              <Else>
                <If condition={canEditBoard}>
                  <Then>
                    <ShareDropdown
                      placement="bottomCenter"
                      pics={pics}
                      title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                        board?.title,
                        45
                      )}相关的设计灵感，来看看：`}
                    >
                      <Button
                        className={classNames([styles.button, styles.rect])}
                        type="text"
                        data-gd-click="button_click"
                        data-button-name="分享"
                        data-board-id={board?.board_id}
                      >
                        <HBIcon className={styles.icon} type="hb_share_new" />
                      </Button>
                    </ShareDropdown>
                    <Button
                      className={classNames([styles.button, styles.rect])}
                      type="text"
                      title="管理采集"
                      data-gd-click="button_click"
                      data-button-name="批量管理"
                      data-board-id={board?.board_id}
                      onClick={goBatch}
                    >
                      <HBIcon className={styles.icon} type="batch1" />
                    </Button>
                    <Button
                      className={classNames([styles.button, styles.rect])}
                      type="text"
                      title="编辑信息"
                      onClick={onBoardEdit}
                      data-gd-click="button_click"
                      data-button-name="编辑"
                      data-board-id={board?.board_id}
                    >
                      <HBIcon className={styles.icon} type="hk_create" />
                    </Button>
                  </Then>
                  <Else>
                    <ShareDropdown
                      placement="bottomCenter"
                      pics={pics}
                      title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                        board?.title,
                        45
                      )}相关的设计灵感，来看看：`}
                    >
                      <Button
                        className={classNames([
                          styles.button,
                          styles.rect,
                          styles.share,
                        ])}
                        type="text"
                        data-gd-click="button_click"
                        data-button-name="分享"
                        data-board-id={board?.board_id}
                        shape="round"
                      >
                        <HBIcon className={styles.icon} type="hb_share_new" />
                      </Button>
                    </ShareDropdown>

                    <FollowButton
                      // className={styles.headerBtn}
                      className={classNames(styles.button, styles.smallBtn)}
                      followed={board?.following}
                      showGrop={
                        board?.user_following !== followType.unIndependent
                      }
                      onClick={onToggleFollow}
                      data-gd-click="button_click"
                      data-button-name={
                        board?.following ? "取消关注" : "关注画板"
                      }
                      data-board-id={board?.board_id}
                      boardInfo={board}
                    />
                  </Else>
                </If>
              </Else>
            </If>
          </div>
        </div>
      </ScrollHeader>

      <BoardPageHeader
        {...boardAction}
        goBatch={goBatch}
        onChangeTab={onChangeTab}
        members={members}
        mutateMembers={mutateMembers}
      />

      {showInvitePage ? (
        <PrivateBoardInviteView
          className={styles.invite}
          board={board}
          inviter={inviter}
          isExpired={isInviteExpired}
          inviteCode={invite_code}
          memberLimit={memberLimit}
          memberTotal={memberTotal}
          onAccept={onInviteAccept}
        />
      ) : (
        <>
          <div ref={tabRef}>
            {inviter && (
              <BoardInviteToast
                board={board}
                inviter={inviter}
                isExpired={isInviteExpired}
                inviteCode={invite_code}
                memberLimit={memberLimit}
                memberTotal={memberTotal}
                onAccept={onInviteAccept}
                onReject={() => {
                  router.push("/boards/" + boardId);
                }}
              />
            )}
            <When condition={board}>
              <Tabs
                className={styles.tabs}
                activeKey={tab}
                destroyInactiveTabPane={true}
              >
                <TabPane tab="采集" key="pin">
                  <BoardPinList
                    board={board}
                    serverSidePins={serverSidePins}
                    query={{
                      viewType,
                      color,
                      fileType,
                      resolution,
                      sort,
                    }}
                  />
                </TabPane>
                <TabPane tab="被关注" key="follow">
                  <BoardUserList board={board} />
                </TabPane>
              </Tabs>
            </When>
          </div>
        </>
      )}
    </div>
  );
}
